const RESTAURANT_ADMIN_PERMISSIONS = ['admin', 'owner', 'adminStandard']
const RESTAURANT_ADMIN_GROUP_TYPES = ['restaurant', 'restaurantGroup']

/**
 * Determine whether a user is eligible to use Restaurant Admin mode.
 *
 * Rules (mirrors backend getUserRestaurantIds logic):
 *   - Platform admin (userLevel === 'admin') → always eligible.
 *   - Has at least one UserGroup with type in ['restaurant','restaurantGroup']
 *     AND a userPermissions entry matching the user's email with an admin-level permission.
 *   - When a `restaurantScope` snapshot is provided (e.g. from GET /user/me/restaurant-scope),
 *     additionally requires at least one associated restaurant to prevent "empty admin-mode UI"
 *     for orphaned UserGroups with no linked Restaurant document.
 *   - When a UserGroup has a populated `restaurant` field (via aggregate populate), we require
 *     at least one matching group to reference a restaurant.
 *
 * @param {object|null} user - The myUser object from the users store.
 * @param {object} [options]
 * @param {{ restaurants?: Array<unknown>, userGroups?: Array<unknown> }} [options.restaurantScope]
 *   Optional scope payload from `GET /user/me/restaurant-scope`. If provided and restaurants
 *   is an empty array (and user is not platform admin), the user is deemed ineligible.
 * @returns {boolean}
 */
export function canUseRestaurantAdminMode(user, options = {}) {
  if (!user) return false
  if (user.userLevel === 'admin') return true

  const groups = user.userGroups || []
  const email = user.email

  const matchingGroups = groups.filter(
    group =>
      RESTAURANT_ADMIN_GROUP_TYPES.includes(group.type) &&
      (group.userPermissions || []).some(
        p => p.email === email && RESTAURANT_ADMIN_PERMISSIONS.includes(p.permission),
      ),
  )

  if (matchingGroups.length === 0) return false

  // Spec §1: must be associated to at least one restaurant. If a restaurantScope snapshot was
  // provided, it is authoritative (non-admins must have at least one restaurant in scope).
  const { restaurantScope } = options
  if (restaurantScope && Array.isArray(restaurantScope.restaurants)) {
    return restaurantScope.restaurants.length > 0
  }

  // Fallback: if any matching group has a populated `restaurant` field (via backend aggregate
  // populate), require at least one to be truthy. Otherwise trust the backend to enforce the
  // full association check at route-level (the /me/restaurant-scope call will gate the UI).
  const anyGroupHasRestaurantField = matchingGroups.some(g => 'restaurant' in g)
  if (anyGroupHasRestaurantField) {
    return matchingGroups.some(g => g.restaurant != null && g.restaurant !== false)
  }

  return true
}
